import { useCallback, useEffect, useMemo, useRef } from 'react'
import { Message } from './Message'
import { PARTICIPANT_COLORS } from './participantColors.js'
import './MessageList.css'

const HOST_THINKING_COLOR = PARTICIPANT_COLORS[5] // blue

export const MessageList = ({
  messages = [],
  isThinking = false,
  isLoadingHistory = false,
  emptyMessage = 'How can I help you today?',
  participants = [],
  contacts = [],
  hostName = 'MyAgent',
}: any) => {
  const nameMap = useMemo(() => {
    const map = new Map(contacts.map((p: any) => [p.roleId, p]))
    for (const p of participants) map.set(p.roleId, p)
    return map
  }, [participants, contacts])
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  const needsInitialScrollRef = useRef(true)

  const scrollToBottom = useCallback((behavior: ScrollBehavior = 'smooth') => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior })
    }
  }, [])

  // biome-ignore lint/correctness/useExhaustiveDependencies: `isThinking` triggers scroll when the thinking indicator appears/disappears
  useEffect(() => {
    if (isLoadingHistory || messages.length === 0) {
      needsInitialScrollRef.current = true
      return
    }
    if (needsInitialScrollRef.current) {
      needsInitialScrollRef.current = false
      scrollToBottom('auto')
      return
    }
    scrollToBottom()
  }, [messages, isThinking, isLoadingHistory, scrollToBottom])

  return (
    <div className="message-list" ref={containerRef}>
      <div className="message-list-content">
        {isLoadingHistory ? (
          <div className="empty-state">
            <div className="thinking-dots">
              <span className="dot" />
              <span className="dot" />
              <span className="dot" />
            </div>
          </div>
        ) : messages.length === 0 ? (
          <div className="empty-state">
            <h1 className="empty-title">{emptyMessage}</h1>
          </div>
        ) : (
          messages.map((msg: any, idx: number) => {
            const participant = msg.participantId ? nameMap.get(msg.participantId) : null
            return (
              <Message
                key={`${msg.timestamp}-${msg.role}-${idx}`}
                role={msg.role}
                content={msg.content}
                timestamp={msg.timestamp}
                isError={msg.isError}
                metadata={msg.metadata}
                participantId={msg.participantId}
                participantName={participant?.displayName || msg.participantId?.slice(0, 8)}
                participantTypeName={participant?.typeName}
                targetName={
                  msg.metadata?.targetId
                    ? nameMap.get(msg.metadata.targetId)?.displayName ||
                      msg.metadata.targetId?.slice(0, 8)
                    : null
                }
                hostName={hostName}
              />
            )
          })
        )}

        {isThinking && (
          <div
            className="message message-participant"
            style={{ '--participant-color': HOST_THINKING_COLOR } as any}
          >
            <div className="message-header">
              <span className="message-role" style={{ color: HOST_THINKING_COLOR }}>
                {hostName}
              </span>
            </div>
            <div className="message-content">
              <div className="thinking-dots">
                <span className="dot" />
                <span className="dot" />
                <span className="dot" />
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>
    </div>
  )
}
